import { FlightStatus } from '../../../shared/types';

// Cache to store flight statuses and reduce API calls
const flightStatusCache: { [key: string]: { data: FlightStatus; timestamp: number } } = {};
const CACHE_DURATION = 60 * 1000; // 1 minute

const getFlightStatusFromCache = (flightNumber: string): FlightStatus | null => {
  const cached = flightStatusCache[flightNumber];
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    return cached.data;
  }
  return null;
};

const setFlightStatusCache = (flightNumber: string, status: FlightStatus) => {
  flightStatusCache[flightNumber] = {
    data: status,
    timestamp: Date.now()
  };
};

// Helper to standardize flight number format
const standardizeFlightNumber = (flightNumber: string): string | null => {
  // Remove any non-alphanumeric characters except spaces
  const cleaned = flightNumber.replace(/[^A-Za-z0-9\s]/g, '').trim().toUpperCase();
  
  // Check if it's a valid flight number pattern (2-3 letters followed by 1-4 numbers)
  const match = cleaned.match(/^([A-Z]{2,3})\s*(\d{1,4})$/);
  if (!match) return null;
  
  // Return standardized format (e.g., "UA 123")
  return `${match[1]} ${match[2]}`;
};

export const getFlightStatus = async (flightNumber: string): Promise<FlightStatus | null> => {
  // Return null for empty flight numbers or non-flight entries
  if (!flightNumber || flightNumber.toLowerCase().includes('n/a') || 
      flightNumber.toLowerCase().includes('driving')) {
    console.log('Skipping non-flight entry:', flightNumber);
    return null;
  }

  try {
    // Check cache first
    const cachedStatus = getFlightStatusFromCache(flightNumber);
    if (cachedStatus) {
      return cachedStatus;
    }

    // Standardize the flight number
    const standardizedNumber = standardizeFlightNumber(flightNumber);
    if (!standardizedNumber) {
      console.log('Invalid flight number format:', flightNumber);
      return null;
    }

    console.log('Fetching status for flight:', standardizedNumber);
    
    // Make request to your backend proxy
    const response = await fetch(`/api/flight-status/${encodeURIComponent(standardizedNumber)}`);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('API Error Response:', errorText);
      
      try {
        const errorData = JSON.parse(errorText);
        // If we get a rate limit error, return null but don't throw
        if (errorData.status === 'error' && (response.status === 429 || errorData.error?.includes('429'))) {
          console.log('Rate limit reached, will retry later');
          return null;
        }
        if (errorData.status === 'error') {
          return null;
        }
      } catch (e) {
        // If we can't parse the error, throw it
        throw new Error(`Flight API request failed: ${response.status}`);
      }
      return null;
    }
    
    const data = await response.json();
    console.log('Flight status response:', data);
    
    if (data.error || data.status === 'error') {
      console.error('API Error:', data.error || 'Unknown error');
      return null;
    }

    // Transform AeroAPI response to our FlightStatus interface
    const flightStatus: FlightStatus = {
      status: determineFlightStatus(data),
      departure: {
        airport: data.origin?.name || 'Unknown',
        scheduled: data.scheduled_out || null,
        actual: data.actual_out || null,
        delay: calculateDelay(data.scheduled_out, data.actual_out)
      },
      arrival: {
        airport: 'Spokane Intl',  // Force Spokane as destination
        scheduled: data.scheduled_in || null,
        actual: data.actual_in || null,
        delay: calculateDelay(data.scheduled_in, data.actual_in)
      }
    };

    // Cache the result
    setFlightStatusCache(standardizedNumber, flightStatus);
    
    return flightStatus;
  } catch (error) {
    console.error('Error fetching flight status:', error);
    return null;
  }
};

const determineFlightStatus = (flight: any): string => {
  if (flight.status === 'error') return 'unknown';
  if (!flight.actual_out) {
    // Format the scheduled time if available
    if (flight.scheduled_out) {
      const scheduledTime = new Date(flight.scheduled_out);
      // Convert to Pacific Time
      const timeString = scheduledTime.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZone: 'America/Los_Angeles'
      });
      return `Scheduled - ${timeString} PT`;
    }
    return 'scheduled';
  }
  if (flight.actual_out && !flight.actual_on) return 'en-route';
  if (flight.diverted) return 'diverted';
  if (flight.actual_out && flight.actual_on) return 'landed';
  if (flight.cancelled) return 'cancelled';
  return 'unknown';
};

const calculateDelay = (scheduled: string | null, actual: string | null): number | null => {
  if (!scheduled || !actual) return null;
  return Math.round((new Date(actual).getTime() - new Date(scheduled).getTime()) / 60000);
}; 